import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Search } from "lucide-react";
import { api, download } from "@/lib/api";
import { dEN } from "@/lib/format";
import { PageHeader, Card, Empty, GhostButton, Field } from "@/components/admin/common";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/admin/DateInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const COLS = [["full_name", "Name"], ["nationality", "Nationality"], ["passport", "Passport"], ["dob", "DOB"], ["cert_level", "Cert."],
  ["logged_dives", "Dives"], ["emergency_phone", "Emergency"], ["medical", "Medical"], ["dietary", "Dietary"], ["cabin", "Cabin"]];

export default function Manifest() {
  const [bookings, setBookings] = useState([]);
  const [bookingId, setBookingId] = useState("all");
  const [date, setDate] = useState("");
  const [q, setQ] = useState("");
  const [guests, setGuests] = useState(null);
  useEffect(() => { api.get("/admin/bookings").then((r) => setBookings(r.data)); }, []);
  const params = { booking_id: bookingId === "all" ? "" : bookingId, date };
  const load = useCallback(() => api.get("/admin/guests", { params: { ...params, q } }).then((r) => setGuests(r.data)), [bookingId, date, q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-7xl" data-testid="manifest-page">
      <PageHeader title="Guest manifest" subtitle="Filter by trip or date, then download for the boat or park ranger."
        actions={<GhostButton onClick={() => download("/admin/guests/export/xlsx", params)} data-testid="manifest-page-export-btn"><Download className="w-4 h-4" />Download Excel</GhostButton>} />
      <Card className="p-4 grid sm:grid-cols-3 gap-3 mb-4">
        <Field label="Trip / booking">
          <Select value={bookingId} onValueChange={(v) => { setBookingId(v); setDate(""); }}>
            <SelectTrigger className="h-12" data-testid="manifest-booking-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All trips</SelectItem>
              {bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.customer_name} · {dEN(b.start_date)}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="On date"><DateInput value={date} onChange={(v) => { setDate(v); if (v) setBookingId("all"); }} testid="manifest-date-input" /></Field>
        <Field label="Search name">
          <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-12 pl-9" data-testid="manifest-search-input" /></div>
        </Field>
      </Card>
      {guests && !guests.length ? <Empty text="No guests match." testid="manifest-empty" /> : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Trip</TableHead>{COLS.map(([k, l]) => <TableHead key={k}>{l}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {guests?.map((g) => (
                <TableRow key={g.id} data-testid={`manifest-row-${g.id}`}>
                  <TableCell className="whitespace-nowrap"><Link to={`/admin/bookings/${g.booking_id}`} className="font-semibold text-oceandeep hover:underline">{g.booking_code}</Link><p className="text-xs text-slate-500">{dEN(g.start_date)} · {g.boat_name || "—"}</p></TableCell>
                  {COLS.map(([k]) => <TableCell key={k} className={!g[k] && ["passport", "cert_level", "emergency_phone"].includes(k) ? "bg-rose-50" : ""}>{g[k] ?? ""}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
